import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import { useFeatureToggle } from 'platform/utilities/feature-toggles';
import RoutedSavableApp from 'platform/forms/save-in-progress/RoutedSavableApp';
import DowntimeNotification, {
  externalServices,
} from 'platform/monitoring/DowntimeNotification';
import { useBrowserMonitoring } from 'platform/monitoring/Datadog';
import environment from 'platform/utilities/environment';

import BreadCrumbs from '../components/BreadCrumbs';
import EnvironmentBanner from '../components/EnvironmentBanner';
import ProgressBar from '../components/ProgressBar';
import ProgressBarErrorBoundary from '../components/ProgressBarErrorBoundary';
import formConfig from '../config/form';
import { dataDog } from '../constants';
import useUtmParamsSync from '../hooks/useUtmParamsSync';

export default function App({ location, children }) {
  const { TOGGLE_NAMES, useToggleLoadingValue, useFormFeatureToggleSync } =
    useFeatureToggle();

  const isLoadingFeatureFlags = useToggleLoadingValue();

  const dataDogConfig = {
    ...dataDog,
    env: environment.vspEnvironment(),
    sessionReplaySampleRate:
      environment.vspEnvironment() === 'staging'
        ? dataDog.replaySampleRateNonProd
        : dataDog.replaySampleRateProd,
  };
  useBrowserMonitoring(dataDogConfig);

  const askVaToggles = useMemo(
    () =>
      Object.entries(TOGGLE_NAMES)
        .filter(([toggleKey]) => toggleKey.startsWith('askVa'))
        .map(([toggleKey, toggleValue]) => ({
          toggleName: toggleKey,
          formKey: `toggle_${toggleValue}`,
        })),
    [TOGGLE_NAMES],
  );

  useFormFeatureToggleSync(askVaToggles);
  useUtmParamsSync(location.search);

  useEffect(() => {
    document.title = 'Ask VA | Veterans Affairs';
  }, []);

  return (
    !isLoadingFeatureFlags && (
      <>
        <EnvironmentBanner />
        <BreadCrumbs currentPath={location.pathname} />
        <DowntimeNotification
          appTitle="Ask VA"
          dependencies={[externalServices.askva]}
        >
          <RoutedSavableApp formConfig={formConfig} currentLocation={location}>
            <ProgressBarErrorBoundary>
              <ProgressBar pathname={location.pathname} />
            </ProgressBarErrorBoundary>
            {children}
          </RoutedSavableApp>
        </DowntimeNotification>
      </>
    )
  );
}

App.propTypes = {
  children: PropTypes.node,
  location: PropTypes.object,
};
